/**
 * InboxColumn — "On Your Plate" for dashboard V2.
 *
 * A calm, glanceable inbox: flat slim rows grouped into three labelled
 * sections — Action needed (SOS / flags), Tagged in (mentions), and Tasks —
 * with a prominent Expand button in the header that opens the full Plate
 * task manager (/home/plate). Entity glyphs + priority dots come from the
 * central entityThemes registry; a sticky quick-add lives at the bottom.
 */

import { useEffect, useMemo, useRef, useState, KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  Siren,
  Flag,
  AtSign,
  CheckCircle2,
  Check,
  ArrowRight,
  ListTodo,
  Maximize2,
  Plus,
  CornerDownLeft,
  CalendarDays,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useMentions } from '@/hooks/useMentions';
import { useTodos } from '@/hooks/useTodos';
import { useTeam } from '@/hooks/useTeam';
import { useSOSAlerts } from '@/hooks/useSOSAlerts';
import { useFlagAlerts } from '@/hooks/useFlagAlerts';
import { useTenantId } from '@/hooks/useTenantId';
import { MentionStatus, MentionEntityType } from '@/types/mention.types';
import { toggleTodo, createTodo } from '@/services/todo.service';
import { extractMentions, createMentions, buildMentionNavPath } from '@/services/mention.service';
import { acknowledgeSOS, acknowledgeFlag } from '@/services/sos.service';
import { AssignPromptModal } from '@/components/todos/AssignPromptModal';
import { PlateOverlay } from '@/components/plate/PlateWorkspace';
import { PriorityBadge } from '@/components/common/PriorityBadge';
import { Avatar } from '@/components/common/Avatar';
import { getEntityTheme } from '@/config/entityThemes';
import { getLocaleSync } from '@/utils/locale';
import type { TodoItem, TodoAssignee, TaskPriority } from '@/types/schema';
import type { MentionableUser, MentionInput } from '@/types/mention.types';
import {
  applyMention,
  findMentionTrigger,
  matchesMentionQuery,
  mentionHandleFor,
} from '@/utils/mention-trigger';
import type { LucideIcon } from 'lucide-react';

type Section = 'action' | 'tagged' | 'tasks';

interface InboxItem {
  id: string;
  section: Section;
  Icon: LucideIcon;
  /** Tinted glyph tile classes (bg + text). */
  tile: string;
  pulse?: boolean;
  /** Optional soft row background (SOS only). */
  rowClass?: string;
  /** Blue dot before the title (unread mentions). */
  unread?: boolean;
  /** Priority dot before the title (urgent / high tasks). */
  priorityDot?: TaskPriority;
  title: string;
  meta?: string;
  action?: { kind: 'check' | 'ack'; tone: 'danger' | 'neutral'; onClick: () => void };
  onClick?: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

function todayDateStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const isOverdueDate = (s?: string) => !!s && s < todayDateStr();
const isTodayDate = (s?: string) => !!s && s === todayDateStr();

/** "2026-07-01" → "1 Jul" (matches PlateCard's due formatting). */
function formatDueLabel(dueDate: string): string {
  const [y, m, d] = dueDate.split('-').map(Number);
  if (!y || !m || !d) return dueDate;
  return new Date(y, m - 1, d).toLocaleDateString(getLocaleSync(), {
    day: 'numeric',
    month: 'short',
  });
}

function relTime(iso?: string): string {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function joinMeta(...parts: (string | undefined)[]): string {
  return parts.filter(Boolean).join(' · ');
}

// ============================================================================
// Main component
// ============================================================================

export function InboxColumn() {
  const navigate = useNavigate();
  const tenantId = useTenantId();
  const userId = localStorage.getItem('user_id') || '';
  const userName = localStorage.getItem('logged_in_user') || '';
  const [expanded, setExpanded] = useState(false);

  const { alerts: sosAlerts, loading: sosLoading } = useSOSAlerts(tenantId);
  const { alerts: flagAlerts, loading: flagLoading } = useFlagAlerts(tenantId);
  const {
    mentions,
    loading: mentionsLoading,
    openMention,
    clearMention,
  } = useMentions({ userId });
  const { todos, loading: todosLoading } = useTodos(userId, tenantId);

  const isInitialLoading = (sosLoading || flagLoading || mentionsLoading || todosLoading) &&
    sosAlerts.length === 0 && flagAlerts.length === 0 && mentions.length === 0 && todos.length === 0;

  const { actionItems, taggedItems, taggedEarlierItems, taskItems } = useMemo(() => {
    const action: InboxItem[] = [];
    const tagged: InboxItem[] = [];
    const taggedEarlier: InboxItem[] = [];
    const tasks: InboxItem[] = [];

    sosAlerts.filter((a) => !a.acknowledged).forEach((alert) => {
      action.push({
        id: `sos-${alert.id}`,
        section: 'action',
        Icon: Siren,
        tile: 'bg-red-500/15 text-red-400',
        pulse: true,
        rowClass: 'bg-red-500/[0.06]',
        title: `SOS — ${alert.techName || 'Tech'}`,
        meta: joinMeta(alert.reason || 'Emergency alert', relTime(alert.triggeredAt)),
        action: {
          kind: 'ack',
          tone: 'danger',
          onClick: () => { if (alert.id) acknowledgeSOS(alert.id, userName, alert.jobId); },
        },
        onClick: () => alert.jobId && navigate(`/home/schedule?jobId=${alert.jobId}`),
      });
    });

    flagAlerts.filter((a) => !a.acknowledged).forEach((alert) => {
      action.push({
        id: `flag-${alert.id}`,
        section: 'action',
        Icon: Flag,
        tile: 'bg-amber-500/15 text-amber-400',
        title: `Flag — ${alert.techName || 'Tech'}`,
        meta: joinMeta(alert.reason || 'Job flagged', relTime(alert.flaggedAt)),
        action: {
          kind: 'ack',
          tone: 'neutral',
          onClick: () => { if (alert.id) acknowledgeFlag(alert.id, userName, alert.jobId); },
        },
        onClick: () => alert.jobId && navigate(`/home/schedule?jobId=${alert.jobId}`),
      });
    });

    // Unread tags stay in "Tagged in"; read ones cascade into the collapsed
    // "Earlier" group so opening something is enough to get it out of the way.
    const pushMention = (mention: (typeof mentions)[number], unread: boolean) => {
      const hasExcerpt = !!mention.noteExcerpt;
      (unread ? tagged : taggedEarlier).push({
        id: `mention-${mention.id}`,
        section: 'tagged',
        Icon: AtSign,
        tile: unread ? 'bg-blue-500/15 text-blue-400' : 'bg-surface-raised text-theme-muted',
        unread,
        title: hasExcerpt ? `“${mention.noteExcerpt}”` : `${mention.createdByName} mentioned you`,
        meta: joinMeta(
          hasExcerpt ? mention.createdByName : undefined,
          mention.entityTitle,
          relTime(mention.createdAt),
        ),
        action: {
          kind: 'check',
          tone: 'neutral',
          onClick: () => { if (mention.id) clearMention(mention.id).catch(() => {}); },
        },
        onClick: () => {
          // Opening it reads it — and every other tag on the same entity.
          openMention(mention).catch(() => {});
          const path = buildMentionNavPath(mention);
          if (path) navigate(path);
        },
      });
    };

    mentions.filter((m) => m.status === MentionStatus.UNREAD).forEach((m) => pushMention(m, true));
    mentions.filter((m) => m.status === MentionStatus.READ).forEach((m) => pushMention(m, false));

    const pushTodo = (todo: TodoItem) => {
      const overdue = isOverdueDate(todo.dueDate);
      const today = isTodayDate(todo.dueDate);
      const theme = getEntityTheme(todo.entityKind);
      const entityLabel = todo.entityKind && todo.entityKind !== 'general' ? theme.label : undefined;
      const dueInfo = overdue
        ? `Overdue · ${formatDueLabel(todo.dueDate!)}`
        : today
        ? 'Due today'
        : todo.dueDate
        ? `Due ${formatDueLabel(todo.dueDate)}`
        : undefined;

      tasks.push({
        id: `todo-${todo.id}`,
        section: 'tasks',
        Icon: theme.Icon,
        // Glyph = entity kind; tile colour carries urgency (overdue red /
        // today amber), otherwise the entity's own tint.
        tile: overdue
          ? 'bg-red-500/15 text-red-400'
          : today
          ? 'bg-amber-500/15 text-amber-400'
          : theme.tile,
        title: todo.title,
        meta: joinMeta(entityLabel, dueInfo),
        priorityDot:
          todo.priority === 'urgent' || todo.priority === 'high' ? todo.priority : undefined,
        action: {
          kind: 'check',
          tone: 'neutral',
          onClick: () => { if (todo.id) toggleTodo(todo.id, true, userId).catch(() => {}); },
        },
        onClick: todo.sourcePath ? () => navigate(todo.sourcePath!) : undefined,
      });
    };

    // Overdue first, then due today, then the rest.
    todos.filter((t) => isOverdueDate(t.dueDate)).forEach(pushTodo);
    todos.filter((t) => isTodayDate(t.dueDate)).forEach(pushTodo);
    todos.filter((t) => !isOverdueDate(t.dueDate) && !isTodayDate(t.dueDate)).forEach(pushTodo);

    return {
      actionItems: action,
      taggedItems: tagged,
      taggedEarlierItems: taggedEarlier,
      taskItems: tasks,
    };
  }, [sosAlerts, flagAlerts, mentions, todos, userId, userName, navigate, openMention, clearMention]);

  // Read tags are deliberately OUT of the count — they're history, not work.
  const totalCount = actionItems.length + taggedItems.length + taskItems.length;
  const subheader = totalCount === 0
    ? 'All caught up — nothing on your plate'
    : actionItems.length > 0
    ? `${actionItems.length} need${actionItems.length === 1 ? 's' : ''} action · ${totalCount} total`
    : `${totalCount} item${totalCount === 1 ? '' : 's'} to handle`;

  return (
    <section className="relative rounded-3xl border border-theme bg-surface-card backdrop-blur-xl flex flex-col h-[640px] max-h-[640px] shadow-[0_8px_32px_-12px_rgba(0,0,0,0.4)] overflow-hidden">
      {/* Apple glass top highlight */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none z-10" />

      {/* Header — title + prominent Expand into the full Plate */}
      <header className="px-5 py-4 border-b border-theme-soft">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-xl bg-brand-secondary/15 text-brand-secondary flex items-center justify-center shrink-0">
              <ListTodo className="w-4 h-4" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-theme-primary leading-tight">On Your Plate</h3>
              <p className="text-[11px] text-theme-muted leading-tight mt-0.5 truncate">{subheader}</p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="group shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-semibold bg-surface-raised text-theme-secondary ring-1 ring-theme-soft hover:text-brand-secondary hover:ring-brand-secondary/30 transition-all active:scale-95"
            aria-label="Expand the full Plate"
            aria-expanded={expanded}
          >
            <Maximize2 className="w-3.5 h-3.5 transition-transform group-hover:scale-110" />
            Expand
          </button>
        </div>
      </header>

      {/* Sectioned list */}
      <div className="flex-1 overflow-y-auto px-2.5 py-2 min-h-0 scrollbar-thin">
        {isInitialLoading ? (
          <SkeletonInboxRows />
        ) : (
          <>
            {totalCount === 0 ? (
              <EmptyCelebration />
            ) : (
              <>
                <SectionBlock label="Action needed" items={actionItems} accent="text-red-400" />
                <SectionBlock label="Tagged in" items={taggedItems} accent="text-blue-400" />
                <SectionBlock label="Tasks" items={taskItems} accent="text-theme-muted" />
              </>
            )}
            {/* Already-read tags — collapsed by default, still one click away. */}
            <CollapsibleSectionBlock label="Earlier" items={taggedEarlierItems} />
          </>
        )}
      </div>

      {/* Sticky quick-add todo */}
      <QuickAddTodo userId={userId} userName={userName} tenantId={tenantId} existingCount={todos.length} />

      {/* Expand-in-place — the full Plate as a PiP-style near-full-screen
          overlay over the dashboard, no route change. Portaled to body: this
          section's backdrop-blur makes it a CONTAINING BLOCK for fixed
          descendants, which would trap + clip the overlay inside the widget. */}
      {createPortal(
        <AnimatePresence>
          {expanded && <PlateOverlay onClose={() => setExpanded(false)} />}
        </AnimatePresence>,
        document.body,
      )}
    </section>
  );
}

// ============================================================================
// SectionBlock + InboxRow — flat, slim, calm
// ============================================================================

function SectionBlock({
  label,
  items,
  accent,
}: {
  label: string;
  items: InboxItem[];
  accent: string;
}) {
  if (items.length === 0) return null;
  return (
    <div className="mb-2 last:mb-0">
      <div className="flex items-center gap-1.5 px-2.5 pt-2 pb-1">
        <span className={`text-[10px] font-bold uppercase tracking-wider ${accent}`}>{label}</span>
        <span className="text-[10px] font-semibold text-theme-muted/70">{items.length}</span>
      </div>
      <div className="space-y-0.5">
        <AnimatePresence initial={false}>
          {items.map((item) => (
            <InboxRow key={item.id} item={item} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

/**
 * Same as SectionBlock but collapsed by default and dimmed — for the
 * already-read pile, which should be reachable without being loud.
 */
function CollapsibleSectionBlock({ label, items }: { label: string; items: InboxItem[] }) {
  const [open, setOpen] = useState(false);

  if (items.length === 0) return null;
  return (
    <div className="mb-2 last:mb-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 px-2.5 pt-2 pb-1 group"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="w-3 h-3 text-theme-muted" />
        ) : (
          <ChevronRight className="w-3 h-3 text-theme-muted" />
        )}
        <span className="text-[10px] font-bold uppercase tracking-wider text-theme-muted">
          {label}
        </span>
        <span className="text-[10px] font-semibold text-theme-muted/70">{items.length}</span>
      </button>
      {open && (
        <div className="space-y-0.5 opacity-75">
          <AnimatePresence initial={false}>
            {items.map((item) => (
              <InboxRow key={item.id} item={item} />
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}

function InboxRow({ item }: { item: InboxItem }) {
  const Icon = item.Icon;
  const clickable = !!item.onClick;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: 24, height: 0, marginBottom: 0 }}
      transition={{ duration: 0.18 }}
      className={`group flex items-center gap-1 rounded-xl overflow-hidden pr-1.5 transition-colors ${
        item.rowClass || ''
      } ${clickable ? 'hover:bg-surface-hover' : ''}`}
    >
      <button
        type="button"
        onClick={item.onClick}
        disabled={!clickable}
        className="flex-1 min-w-0 flex items-center gap-2.5 text-left px-2.5 py-2 rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-secondary/40 disabled:cursor-default"
      >
        <span className={`w-7 h-7 rounded-lg ${item.tile} flex items-center justify-center shrink-0`}>
          {item.pulse ? (
            <motion.span
              animate={{ scale: [1, 1.15, 1] }}
              transition={{ duration: 1.1, repeat: Infinity }}
              className="flex"
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
            </motion.span>
          ) : (
            <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
          )}
        </span>

        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1.5">
            {item.unread && <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" aria-label="Unread" />}
            {item.priorityDot && (
              <PriorityBadge priority={item.priorityDot} variant="dot" className="shrink-0" />
            )}
            <span className="text-[13px] font-medium text-theme-primary truncate leading-snug">
              {item.title}
            </span>
          </span>
          {item.meta && (
            <span className="block text-[11px] text-theme-muted truncate mt-0.5">{item.meta}</span>
          )}
        </span>

        {clickable && (
          <ArrowRight className="w-3.5 h-3.5 text-transparent group-hover:text-theme-muted/60 shrink-0 transition-colors" />
        )}
      </button>

      {item.action &&
        (item.action.kind === 'check' ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); item.action!.onClick(); }}
            aria-label="Done"
            title="Done"
            className="-m-1 p-2.5 shrink-0 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/50"
          >
            <span className="w-[18px] h-[18px] rounded-full border border-theme hover:border-emerald-400 hover:bg-emerald-500/10 text-transparent hover:text-emerald-400 flex items-center justify-center transition-colors">
              <Check className="w-3 h-3" strokeWidth={3} />
            </span>
          </button>
        ) : (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); item.action!.onClick(); }}
            className={`shrink-0 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold transition-all active:scale-95 ${
              item.action.tone === 'danger'
                ? 'bg-red-500/15 text-red-400 hover:bg-red-500/25 ring-1 ring-red-400/20'
                : 'bg-surface-raised text-theme-secondary hover:bg-surface-hover ring-1 ring-theme-soft'
            }`}
          >
            Ack
          </button>
        ))}
    </motion.div>
  );
}

// ============================================================================
// QuickAddTodo — sticky bottom input with @mention autocomplete + handoff
// ============================================================================

function QuickAddTodo({
  userId,
  userName,
  tenantId,
  existingCount,
}: {
  userId: string;
  userName: string;
  tenantId: string;
  existingCount: number;
}) {
  const { team } = useTeam();
  const [title, setTitle] = useState('');
  const [dueToday, setDueToday] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // @ autocomplete state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState<MentionableUser[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);

  // Assign prompt
  const [showAssignPrompt, setShowAssignPrompt] = useState(false);
  const [pendingTitle, setPendingTitle] = useState('');
  const [pendingDueToday, setPendingDueToday] = useState(false);
  const [pendingMentions, setPendingMentions] = useState<MentionInput[]>([]);

  // Map team to MentionableUser
  const availableUsers: MentionableUser[] = useMemo(
    () =>
      team.map((m) => ({
        id: m.id,
        name: m.name,
        firstName: m.name.split(' ')[0],
        lastName: m.name.split(' ').slice(1).join(' '),
        role: m.dualAccess ? 'Office' : m.role,
        isActive: m.active,
      })),
    [team],
  );

  // Detect @ in input → show suggestions
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;

    const cursor = el.selectionStart ?? title.length;
    // Shared trigger rules (src/utils/mention-trigger.ts): an "@" opens the
    // picker whenever it starts a word — mid-sentence and after punctuation
    // both work, while an email address in the text stays quiet.
    const trigger = findMentionTrigger(title, cursor);
    if (!trigger) {
      setShowSuggestions(false);
      return;
    }
    const filtered = availableUsers.filter((u) => matchesMentionQuery(u, trigger.query));
    if (filtered.length > 0) {
      setSuggestions(filtered);
      setShowSuggestions(true);
      setSelectedIdx(0);
    } else {
      setShowSuggestions(false);
    }
  }, [title, availableUsers]);

  const insertMention = (user: MentionableUser) => {
    const el = inputRef.current;
    if (!el) return;
    const cursor = el.selectionStart ?? title.length;
    const trigger = findMentionTrigger(title, cursor);
    if (!trigger) return;

    const next = applyMention(
      title,
      cursor,
      trigger.atIndex,
      mentionHandleFor(user, availableUsers),
    );
    setTitle(next.text);
    setShowSuggestions(false);

    setTimeout(() => {
      el.setSelectionRange(next.cursor, next.cursor);
      el.focus();
    }, 0);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIdx((p) => (p + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIdx((p) => (p - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && suggestions.length > 0)) {
        e.preventDefault();
        insertMention(suggestions[selectedIdx]);
        return;
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        return;
      }
    }
    if (e.key === 'Enter' && !showSuggestions && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    const trimmed = title.trim();
    if (!trimmed || submitting || !userId) return;

    // Detect mentions for other users
    const mentions = extractMentions(trimmed, availableUsers);
    const others = mentions.filter((m) => m.userId !== userId);

    if (others.length > 0) {
      // Prompt user: keep me on it too, or hand it off
      setPendingTitle(trimmed);
      setPendingDueToday(dueToday);
      setPendingMentions(others);
      setShowAssignPrompt(true);
      setTitle('');
      setDueToday(false);
    } else {
      await persistTodo(trimmed, dueToday, [], true);
      setTitle('');
      setDueToday(false);
      inputRef.current?.focus();
    }
  };

  const persistTodo = async (
    text: string,
    todayFlag: boolean,
    extraAssignees: TodoAssignee[],
    keepCreator: boolean,
  ) => {
    setSubmitting(true);
    try {
      const userRole = localStorage.getItem('user_role') || 'Office';
      const now = new Date().toISOString();
      const assignees: TodoAssignee[] = [];

      if (keepCreator) {
        assignees.push({ userId, userName, role: userRole, addedAt: now });
      }
      extraAssignees.forEach((a) => {
        if (!assignees.some((x) => x.userId === a.userId)) assignees.push(a);
      });

      const todoId = await createTodo({
        tenantId,
        title: text,
        completed: false,
        order: existingCount,
        createdBy: userId,
        createdByName: userName,
        assignees,
        ...(todayFlag ? { dueDate: todayDateStr() } : {}),
      });

      if (extraAssignees.length > 0) {
        createMentions({
          noteContent: text,
          entityType: MentionEntityType.TODO,
          entityId: todoId,
          entityTitle: `Todo: ${text.slice(0, 60)}`,
          createdBy: userId,
          createdByName: userName,
          tenantId,
          availableUsers,
        }).catch(() => {});
      }
    } catch {
      // keep input intact on failure so user can retry
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignConfirm = async (keepCreator: boolean) => {
    const now = new Date().toISOString();
    const extras: TodoAssignee[] = pendingMentions.map((m) => {
      const member = team.find((t) => t.id === m.userId);
      const role = member?.dualAccess ? 'Office' : (member?.role || 'Office');
      return { userId: m.userId, userName: m.userName, role, addedAt: now };
    });

    await persistTodo(pendingTitle, pendingDueToday, extras, keepCreator);
    setShowAssignPrompt(false);
    setPendingMentions([]);
    setPendingTitle('');
    setPendingDueToday(false);
  };

  return (
    <>
      <div className="relative border-t border-theme-soft bg-surface-card/80 backdrop-blur-xl p-3">
        {/* Suggestions dropdown */}
        <AnimatePresence>
          {showSuggestions && suggestions.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.12 }}
              className="absolute left-3 right-3 bottom-full mb-2 rounded-xl border border-theme bg-surface-card backdrop-blur-xl shadow-xl overflow-hidden z-20"
            >
              {suggestions.slice(0, 6).map((user, i) => (
                <button
                  key={user.id}
                  type="button"
                  onMouseDown={(e) => { e.preventDefault(); insertMention(user); }}
                  onMouseEnter={() => setSelectedIdx(i)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                    i === selectedIdx ? 'bg-brand-secondary/15' : 'hover:bg-surface-hover'
                  }`}
                >
                  <Avatar name={user.name} userId={user.id} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-theme-primary truncate">{user.name}</p>
                    <p className="text-[10px] text-theme-muted">{user.role}</p>
                  </div>
                  <AtSign className="w-3 h-3 text-theme-muted/50" />
                </button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center gap-2 rounded-xl bg-surface-raised ring-1 ring-theme-soft focus-within:ring-2 focus-within:ring-brand-secondary/50 transition-all px-3 py-2">
          <button
            type="button"
            onClick={() => inputRef.current?.focus()}
            className="shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br from-brand-secondary/20 to-brand-secondary/5 flex items-center justify-center text-brand-secondary"
            aria-label="Quick add a todo"
          >
            <Plus className="w-4 h-4" strokeWidth={2.5} />
          </button>
          <input
            ref={inputRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Quick add a todo... use @ to tag someone"
            disabled={submitting}
            className="flex-1 min-w-0 bg-transparent text-sm text-theme-primary placeholder:text-theme-muted focus:outline-none disabled:opacity-50"
          />
          <button
            type="button"
            onClick={() => setDueToday((v) => !v)}
            className={`shrink-0 flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-md transition-all ${
              dueToday
                ? 'bg-amber-500/15 text-amber-400 ring-1 ring-amber-400/20'
                : 'text-theme-muted hover:text-theme-secondary hover:bg-surface-hover'
            }`}
            aria-pressed={dueToday}
          >
            <CalendarDays className="w-3 h-3" />
            Today
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!title.trim() || submitting}
            className={`shrink-0 flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-md transition-all ${
              title.trim() && !submitting
                ? 'bg-brand-secondary text-brand-primary hover:brightness-110 active:scale-95'
                : 'bg-surface-hover text-theme-muted/50 cursor-not-allowed'
            }`}
            aria-label="Add todo"
          >
            <CornerDownLeft className="w-3 h-3" strokeWidth={2.5} />
          </button>
        </div>
      </div>

      <AssignPromptModal
        isOpen={showAssignPrompt}
        onClose={() => {
          // restore input on cancel
          setTitle(pendingTitle);
          setDueToday(pendingDueToday);
          setShowAssignPrompt(false);
          setPendingMentions([]);
          setPendingTitle('');
          setPendingDueToday(false);
        }}
        onConfirm={handleAssignConfirm}
        mentions={pendingMentions}
      />
    </>
  );
}

// ============================================================================
// Skeleton + empty celebration
// ============================================================================

function SkeletonInboxRows() {
  return (
    <div className="space-y-1 animate-pulse px-1 pt-1">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="flex items-center gap-2.5 px-2.5 py-2"
          style={{ opacity: 1 - i * 0.15 }}
        >
          <div className="w-7 h-7 rounded-lg bg-surface-hover shrink-0" />
          <div className="flex-1 min-w-0 space-y-1.5">
            <div className="h-3 bg-surface-hover rounded w-2/3" />
            <div className="h-2 bg-surface-hover/60 rounded w-1/3" />
          </div>
          <div className="w-[18px] h-[18px] rounded-full bg-surface-hover/60 shrink-0" />
        </div>
      ))}
    </div>
  );
}

function EmptyCelebration() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 280, damping: 24 }}
      className="flex flex-col items-center justify-center py-16 gap-4"
    >
      <motion.div
        animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.05, 1] }}
        transition={{ duration: 2.5, repeat: Infinity }}
        className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/30 relative"
      >
        <CheckCircle2 className="w-10 h-10 text-white" strokeWidth={2.5} />
        <motion.div
          className="absolute -inset-1 rounded-2xl border-2 border-emerald-400"
          animate={{ scale: [1, 1.15, 1], opacity: [0.6, 0, 0.6] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </motion.div>
      <div className="text-center">
        <p className="text-base font-bold text-theme-primary">Inbox to zero</p>
        <p className="text-xs text-theme-muted mt-1.5 max-w-[260px]">
          Nothing on your plate. Add one below or go knock something off the long-term list.
        </p>
      </div>
      <motion.div
        animate={{ y: [0, -3, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <Sparkles className="w-4 h-4 text-emerald-400/60" />
      </motion.div>
    </motion.div>
  );
}
